import { ChatRequest, ChatResponse, IssueAnalysisRequest, IssueAnalysisResponse, SuggestRequest, SuggestResponse } from '../types';
import { authType, getIdToken } from './auth';

export async function openAuthenticatedPdf(url: string): Promise<void> {
    const token = await getIdToken();
    const headers: Record<string, string> = {};
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const hash = new URL(url, window.location.origin).hash;

    const response = await fetch(url, { headers });
    if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl + hash, '_blank', 'noopener,noreferrer');
}

export class ApiError extends Error {
    constructor(message: string, public status?: number) {
        super(message);
        this.name = 'ApiError';
    }
}

export const apiClient = {
    async chat(request: IssueAnalysisRequest | ChatRequest): Promise<IssueAnalysisResponse | ChatResponse> {
        const API_BASE_URL = import.meta.env.VITE_API_URL || '';

        if (!API_BASE_URL) {
            throw new ApiError('API URL not configured');
        }

        const token = await getIdToken();
        const isMockAuth = import.meta.env.VITE_MOCK_AUTH === 'true';

        if (!token && authType === 'aws' && !isMockAuth) {
            throw new ApiError('Not authenticated', 401);
        }

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        try {
            // API_BASE_URL already includes /chat endpoint, don't append it again
            const response = await fetch(API_BASE_URL, {
                method: 'POST',
                headers,
                body: JSON.stringify(request),
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new ApiError('Unauthorized - please sign in again', 401);
                }
                throw new ApiError(
                    `API request failed: ${response.status} ${response.statusText}`,
                    response.status
                );
            }

            const data: IssueAnalysisResponse | ChatResponse = await response.json();
            console.log('[API] Raw response received:', {
                has_answer: 'answer' in data && !!data.answer,
                answer_length: 'answer' in data ? (data.answer?.length || 0) : 0,
                has_formatted_issues: 'formatted_issues' in data && !!(data as IssueAnalysisResponse).formatted_issues,
                formatted_issues_length: 'formatted_issues' in data ? ((data as IssueAnalysisResponse).formatted_issues?.length || 0) : 0,
                has_prompt: 'prompt' in data && !!(data as IssueAnalysisResponse).prompt,
                in_qa_mode: 'in_qa_mode' in data ? (data as IssueAnalysisResponse).in_qa_mode : undefined,
                issues_count: 'issues' in data ? ((data as IssueAnalysisResponse).issues?.length || 0) : 0,
                all_keys: Object.keys(data)
            });
            return data;
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    },

    async suggest(query: string): Promise<string[]> {
        const API_BASE_URL = import.meta.env.VITE_API_URL || '';

        if (!API_BASE_URL) {
            return [];
        }

        const token = await getIdToken();
        const isMockAuth = import.meta.env.VITE_MOCK_AUTH === 'true';

        if (!token && authType === 'aws' && !isMockAuth) {
            return [];
        }

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        try {
            let suggestUrl: string;
            try {
                const url = new URL(API_BASE_URL);
                url.searchParams.set('suggest', 'true');
                suggestUrl = url.toString();
            } catch {
                const separator = API_BASE_URL.includes('?') ? '&' : '?';
                suggestUrl = `${API_BASE_URL}${separator}suggest=true`;
            }

            const response = await fetch(suggestUrl, {
                method: 'POST',
                headers,
                body: JSON.stringify({ query } as SuggestRequest),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Suggest API error (${response.status}):`, errorText);
                return [];
            }

            const data: SuggestResponse = await response.json();
            console.log(`Received ${data.suggestions?.length || 0} suggestions for query: "${query}"`);
            return data.suggestions || [];
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            return [];
        }
    },
};
